// Notification helper functions for typeKey based operations

export interface NotificationParams {
  [key: string]: string;
}

export const retiredModuleNotificationTypes = new Set([
  'translationTool',
  'translateRequestCreated',
  'translateRequestApproved',
  'newMessageCreated',
  'audioVideoSeparation',
  'trimComplete',
  'restoration_completed',
]);

// API'den gelen model ile uyumlu interface
export interface NotificationItem {
  id: string;
  type: string;
  typeKey: string;
  params?: Record<string, string>;
  isRead: boolean;
  creationTime: string;
  slug?: string;
}

// TypeKey based message generators
export const generateNotificationMessage = (
  typeKey: string,
  params: NotificationParams = {},
  t: (key: string, options?: any) => string
): string => {
  switch (typeKey) {
    case 'copyright':
      return t('pages/notifications:pages.notifications.messages.copyright', {
        channelTitle: params.channelTitle || t('common:common.unknown_channel'),
        copyrightCount: params.claimcount || '1',
      });

    case 'general':
      return params.message || t('pages/notifications:pages.notifications.messages.general');

    case 'system':
      return params.message || t('pages/notifications:pages.notifications.messages.system');

    case 'warning':
      return params.message || t('pages/notifications:pages.notifications.messages.warning');

    case 'monetization':
      return t('pages/notifications:pages.notifications.messages.monetization', {
        channelTitle: params.channelTitle || t('common:common.unknown_channel'),
        status: params.status || t('common:common.status_updated'),
      });

    case 'content_id':
      return t('pages/notifications:pages.notifications.messages.contentId', {
        channelTitle: params.channelTitle || t('common:common.unknown_channel'),
        matchCount: params.matchCount || '1',
      });

    case 'strike':
      return t('pages/notifications:pages.notifications.messages.strike', {
        channelTitle: params.channelTitle || t('common:common.unknown_channel'),
        strikeCount: params.strikeCount || '1',
      });

    case 'channelSubscriber': {
      const count = Number(params.subscriberCount ?? 1);

      const formattedCount = new Intl.NumberFormat('en', {
        notation: 'compact',
        compactDisplay: 'short',
        maximumFractionDigits: 1, // 1.7M gibi
      }).format(count);

      return t('pages/notifications:pages.notifications.messages.channelSubscriber', {
        channelTitle: params.channelTitle || t('common:common.unknown_channel'),
        subscriberCount: formattedCount,
      });
    }

    case 'translateRequestCreated':
      return t('pages/notifications:pages.notifications.messages.translateRequestCreated');

    case 'translateRequestApproved':
      return t('pages/notifications:pages.notifications.messages.translateRequestApproved');

    case 'newMessageCreated': {
      return t('pages/notifications:pages.notifications.messages.newMessageCreated');
    }

    case 'policyStatusCheck': {
      return t('pages/notifications:pages.notifications.messages.policyStatusCheck', {
        videoTitle: params.videoTitle || t('common:common.unknown_video'),
        channelTitle: params.channelTitle || t('common:common.unknown_channel'),
      });
    }
    case 'audioVideoSeparation': {
      return t('pages/notifications:pages.notifications.messages.audioVideoSeparation', {
        videoTitle: params.videoTitle || t('common:common.unknown_video'),
        channelTitle: params.channelTitle || t('common:common.unknown_channel'),
      });
    }
    case 'trimComplete': {
      return t('pages/notifications:pages.notifications.messages.trimComplete', {
        videoTitle: params.videoTitle || t('common:common.unknown_video'),
        channelTitle: params.channelTitle || t('common:common.unknown_channel'),
      });
    }

    case 'restoration_completed': {
      return t('pages/notifications:pages.notifications.messages.restoration_completed', {
        videoTitle: params.title || t('common:common.unknown_video'),
      });
    }

    case 'live_stream_health_status_changed_to_bad': {
      return t('pages/notifications:pages.notifications.messages.liveStreamCriticalLevel', {
        liveStream_title: params.title,
      });
    }

    case 'live_stream_health_status_changed_to_noData': {
      return t('pages/notifications:pages.notifications.messages.liveStreamDataUnavailable', {
        liveStream_title: params.title,
      });
    }

    case '13': {
      return t('pages/notifications:pages.notifications.messages.13', {
        title: params.title || t('common:common.unknown_title'),
      });
    }

    case 'workReportUnapprovedReminder': {
      return t('pages/notifications:pages.notifications.messages.workReportUnapprovedReminder', {
        title: params.title || t('common:common.unknown_title'),
      });
    }

    default: {
      // Eğer bilinmeyen bir typeKey ise, params'ları göster
      if (params && Object.keys(params).length > 0) {
        const paramsList = Object.entries(params)
          .map(([key, value]) => `${key}: ${value}`)
          .join(', ');
        return paramsList;
      }
      return typeKey;
    }
  }
};

// Generate notification title for navbar
export const generateNotificationTitle = (
  typeKey: string,
  params: NotificationParams = {},
  t: (key: string, options?: any) => string
): string => {
  switch (typeKey) {
    case 'copyright': {
      const claimCount = params.claimcount || '1';
      return t('pages/notifications:pages.notifications.titles.copyright', { count: claimCount });
    }

    case 'general':
      return t('pages/notifications:pages.notifications.titles.general');

    case 'system':
      return t('pages/notifications:pages.notifications.titles.system');

    case 'warning':
      return t('pages/notifications:pages.notifications.titles.warning');

    case 'monetization':
      return t('pages/notifications:pages.notifications.titles.monetization');

    case 'content_id':
      return t('pages/notifications:pages.notifications.titles.contentId');

    case 'strike':
      return t('pages/notifications:pages.notifications.titles.strike');

    case 'channelSubscriber':
      return t('pages/notifications:pages.notifications.titles.channelSubscriber');

    case 'translateRequestCreated':
      return t('pages/notifications:pages.notifications.titles.translateRequestCreated');

    case 'translateRequestApproved':
      return t('pages/notifications:pages.notifications.titles.translateRequestApproved');

    case 'newMessageCreated':
      return t('pages/notifications:pages.notifications.titles.newMessageCreated');

    case 'policyStatusCheck':
      return t('pages/notifications:pages.notifications.titles.policyStatusCheck');
    case 'audioVideoSeparation':
      return t('pages/notifications:pages.notifications.titles.audioVideoSeparation');
    case 'trimComplete':
      return t('pages/notifications:pages.notifications.titles.trimComplete');
    case 'restoration_completed':
      return t('pages/notifications:pages.notifications.titles.restoration_completed');
    case 'live_stream_health_status_changed_to_bad':
      return t('pages/notifications:pages.notifications.titles.liveStreamCriticalLevel');
    case 'live_stream_health_status_changed_to_noData':
      return t('pages/notifications:pages.notifications.titles.liveStreamDataUnavailable');
    case '13':
      return t('pages/notifications:pages.notifications.titles.13');
    case 'workReportUnapprovedReminder':
      return t('pages/notifications:pages.notifications.titles.workReportUnapprovedReminder');
    default:
      return t('pages/notifications:pages.notifications.titles.default', { type: typeKey });
  }
};

// Search in notification content
export const searchInNotification = (
  notification: NotificationItem,
  searchTerm: string,
  t: (key: string, options?: any) => string
): boolean => {
  if (!searchTerm || searchTerm.trim() === '') {
    return true;
  }

  const searchTermLower = searchTerm.toLowerCase();

  // Type ve typeKey'de ara - null check ile
  const typeMatch = notification.type
    ? notification.type.toLowerCase().includes(searchTermLower)
    : false;
  const typeKeyMatch = notification.typeKey
    ? notification.typeKey.toLowerCase().includes(searchTermLower)
    : false;

  // Generated message'da ara
  const message = generateNotificationMessage(notification.typeKey, notification.params || {}, t);
  const messageMatch = message.toLowerCase().includes(searchTermLower);

  // Title'da ara
  const title = generateNotificationTitle(notification.typeKey, notification.params || {}, t);
  const titleMatch = title.toLowerCase().includes(searchTermLower);

  // Params değerlerinde ara - null check ile
  const paramsMatch = notification.params
    ? Object.values(notification.params).some((value) =>
        value ? value.toLowerCase().includes(searchTermLower) : false
      )
    : false;

  return typeMatch || typeKeyMatch || messageMatch || titleMatch || paramsMatch;
};
